/**
 * Types du domaine. Aucun import de React, de Dexie ou d'API navigateur ici :
 * ce fichier est partage tel quel par l'UI et par le moteur d'analyse.
 */

/**
 * Instant precis, stocke en ISO 8601 **UTC** (suffixe `Z`).
 *
 * Le choix de l'UTC n'est pas cosmetique : une chaine ISO avec decalage local
 * (`+01:00` l'hiver, `+02:00` l'ete) ne se trie plus correctement en ordre
 * lexicographique au passage a l'heure d'ete, ce qui casserait a la fois les
 * index Dexie et les comparaisons de fenetres du moteur d'analyse.
 */
export type Instant = string

/** Jour calendaire **local**, au format `YYYY-MM-DD`. */
export type DayKey = string

export type Id = string

// ---------------------------------------------------------------------------
// Ingredients
// ---------------------------------------------------------------------------

/** Familles de FODMAP suivies. */
export type FodmapFamily =
  | 'fructanes'
  | 'gos'
  | 'lactose'
  | 'fructose'
  | 'polyols'

export const FODMAP_FAMILIES: readonly FodmapFamily[] = [
  'fructanes',
  'gos',
  'lactose',
  'fructose',
  'polyols',
] as const

export const FODMAP_FAMILY_LABELS: Record<FodmapFamily, string> = {
  fructanes: 'Fructanes',
  gos: 'GOS (galacto-oligosaccharides)',
  lactose: 'Lactose',
  fructose: 'Fructose excédentaire',
  polyols: 'Polyols',
}

export type FodmapLevel = 'faible' | 'modere' | 'eleve' | 'inconnu'

export const FODMAP_LEVELS: readonly FodmapLevel[] = [
  'faible',
  'modere',
  'eleve',
  'inconnu',
] as const

export const FODMAP_LEVEL_LABELS: Record<FodmapLevel, string> = {
  faible: 'Faible',
  modere: 'Modéré',
  eleve: 'Élevé',
  inconnu: 'Inconnu',
}

/**
 * Poids utilise pour convertir un niveau FODMAP en charge quantitative.
 * Sert au calcul de l'effet-dose (somme `qte_en_grammes x poids`).
 */
export const FODMAP_LEVEL_WEIGHT: Record<FodmapLevel, number> = {
  faible: 0.1,
  modere: 0.5,
  eleve: 1,
  inconnu: 0,
}

export type IngredientCategory =
  | 'legume'
  | 'fruit'
  | 'cereale'
  | 'legumineuse'
  | 'laitage'
  | 'viande'
  | 'poisson'
  | 'oeuf'
  | 'matiere-grasse'
  | 'noix-graine'
  | 'epice-herbe'
  | 'condiment-sauce'
  | 'sucrant'
  | 'boisson'
  | 'alcool'
  | 'plat-prepare'
  | 'autre'

export const INGREDIENT_CATEGORIES: readonly IngredientCategory[] = [
  'legume',
  'fruit',
  'cereale',
  'legumineuse',
  'laitage',
  'viande',
  'poisson',
  'oeuf',
  'matiere-grasse',
  'noix-graine',
  'epice-herbe',
  'condiment-sauce',
  'sucrant',
  'boisson',
  'alcool',
  'plat-prepare',
  'autre',
] as const

export const INGREDIENT_CATEGORY_LABELS: Record<IngredientCategory, string> = {
  legume: 'Légumes',
  fruit: 'Fruits',
  cereale: 'Céréales & féculents',
  legumineuse: 'Légumineuses',
  laitage: 'Produits laitiers',
  viande: 'Viandes',
  poisson: 'Poissons & fruits de mer',
  oeuf: 'Œufs',
  'matiere-grasse': 'Matières grasses',
  'noix-graine': 'Noix & graines',
  'epice-herbe': 'Épices & herbes',
  'condiment-sauce': 'Condiments & sauces',
  sucrant: 'Sucres & sucrants',
  boisson: 'Boissons',
  alcool: 'Alcools',
  'plat-prepare': 'Plats préparés',
  autre: 'Autre',
}

export interface Ingredient {
  id: Id
  nom: string
  categorie: IngredientCategory
  niveauFodmap: FodmapLevel
  famillesFodmap: FodmapFamily[]
  /** Termes alternatifs pour la recherche (pluriels, synonymes regionaux). */
  synonymes: string[]
  /** `true` si cree par l'utilisateur, `false` si issu du referentiel livre. */
  perso: boolean
  /** L'utilisateur peut masquer un ingredient du referentiel sans le supprimer. */
  archive?: boolean
  creeLe: Instant
  modifieLe: Instant
}

// ---------------------------------------------------------------------------
// Unites & quantites
// ---------------------------------------------------------------------------

export type Unite = 'g' | 'ml' | 'unite' | 'cas' | 'cac' | 'pincee' | 'portion'

export const UNITES: readonly Unite[] = ['g', 'ml', 'unite', 'cas', 'cac', 'pincee', 'portion'] as const

export const UNITE_LABELS: Record<Unite, string> = {
  g: 'g',
  ml: 'ml',
  unite: 'unité(s)',
  cas: 'c. à soupe',
  cac: 'c. à café',
  pincee: 'pincée(s)',
  portion: 'portion(s)',
}

/**
 * Conversion approximative vers des grammes, pour le calcul de charge FODMAP.
 *
 * Ces equivalences sont volontairement grossieres : l'analyse compare des
 * charges relatives entre repas d'un meme utilisateur, pas des valeurs
 * nutritionnelles absolues. Une erreur systematique s'annule donc en grande
 * partie dans la comparaison.
 */
export const UNITE_EN_GRAMMES: Record<Unite, number> = {
  g: 1,
  ml: 1,
  unite: 80,
  cas: 15,
  cac: 5,
  pincee: 1,
  portion: 150,
}

export interface Quantite {
  qte: number
  unite: Unite
}

export function quantiteEnGrammes(q: Quantite): number {
  return q.qte * (UNITE_EN_GRAMMES[q.unite] ?? 1)
}

// ---------------------------------------------------------------------------
// Recettes
// ---------------------------------------------------------------------------

export interface RecipeItem extends Quantite {
  ingredientId: Id
}

export interface Recipe {
  id: Id
  nom: string
  /** Nombre de portions que produit la recette telle qu'elle est saisie. */
  portions: number
  items: RecipeItem[]
  favorite: boolean
  archivee: boolean
  notes?: string
  creeLe: Instant
  modifieLe: Instant
}

// ---------------------------------------------------------------------------
// Repas
// ---------------------------------------------------------------------------

export type MealType = 'petit-dej' | 'dejeuner' | 'diner' | 'collation'

export const MEAL_TYPES: readonly MealType[] = ['petit-dej', 'dejeuner', 'diner', 'collation'] as const

export const MEAL_TYPE_LABELS: Record<MealType, string> = {
  'petit-dej': 'Petit-déjeuner',
  dejeuner: 'Déjeuner',
  diner: 'Dîner',
  collation: 'Collation',
}

/** Ce que l'utilisateur a selectionne : une recette (avec un nombre de portions) ou un ingredient seul. */
export type MealSource =
  | { kind: 'recipe'; recipeId: Id; portions: number }
  | { kind: 'ingredient'; ingredientId: Id; qte: number; unite: Unite }

export interface Meal {
  id: Id
  at: Instant
  type: MealType
  sources: MealSource[]
  /**
   * Composition figée au moment de la saisie.
   *
   * **Ne jamais recalculer depuis les recettes courantes.** Une recette evolue
   * (on en retire l'oignon six mois plus tard) ; re-developper la recette
   * actuelle changerait retroactivement la composition des repas passes et
   * fausserait silencieusement toutes les statistiques.
   */
  ingredientsResolus: RecipeItem[]
  notes?: string
  creeLe: Instant
  modifieLe: Instant
}

// ---------------------------------------------------------------------------
// Symptomes
// ---------------------------------------------------------------------------

/**
 * ## Deux natures d'evenement, deliberement separees
 *
 * Une **sensation** (douleur, ballonnement) est un etat qui *dure* : elle a un
 * debut, une fin et une intensite. Un **passage aux selles** est un evenement
 * *ponctuel*, decrit par l'echelle de Bristol et un caractere urgent ou non.
 *
 * Les confondre, comme le faisait une premiere version, empeche de repondre a
 * la question la plus simple et la plus utile du suivi : « combien de passages
 * aujourd'hui ? ». Une frequence ne se deduit pas d'un modele a duree.
 *
 * Les deux alimentent l'analyse : les selles y entrent sous forme d'episodes
 * *derives* (cf. `analysis/selles.ts`), ce qui laisse le moteur inchange.
 */

/** Ce que l'utilisateur saisit comme ressenti, avec une duree. */
export type SensationType = 'ballonnements' | 'douleurs' | 'gaz' | 'nausee' | 'reflux' | 'fatigue'

export const SENSATION_TYPES: readonly SensationType[] = [
  'ballonnements',
  'douleurs',
  'gaz',
  'nausee',
  'reflux',
  'fatigue',
] as const

/** Types deduits des passages aux selles, jamais saisis directement. */
export type SelleDeriveType =
  | 'selles-molles'
  | 'selles-dures'
  | 'urgence'
  | 'evacuation-incomplete'
  | 'douleur-defecation'

export const SELLE_DERIVE_TYPES: readonly SelleDeriveType[] = [
  'selles-molles',
  'selles-dures',
  'urgence',
  'evacuation-incomplete',
  'douleur-defecation',
] as const

/** Ce que voit le moteur d'analyse : sensations saisies + types derives. */
export type SymptomType = SensationType | SelleDeriveType

export const SYMPTOM_TYPES: readonly SymptomType[] = [
  ...SENSATION_TYPES,
  ...SELLE_DERIVE_TYPES,
] as const

export const SYMPTOM_LABELS: Record<SymptomType, string> = {
  ballonnements: 'Ballonnements / distension',
  douleurs: 'Douleurs abdominales',
  gaz: 'Gaz / flatulences',
  nausee: 'Nausée',
  reflux: 'Reflux / brûlures',
  fatigue: 'Fatigue',
  'selles-molles': 'Selles molles ou liquides (Bristol 6-7)',
  'selles-dures': 'Selles dures (Bristol 1-2)',
  urgence: 'Urgence défécatoire',
  'evacuation-incomplete': 'Évacuation incomplète',
  'douleur-defecation': 'Douleur à la défécation',
}

/** Echelle de Bristol (1 = billes dures, 7 = entierement liquide). */
export type Bristol = 1 | 2 | 3 | 4 | 5 | 6 | 7

export const BRISTOLS: readonly Bristol[] = [1, 2, 3, 4, 5, 6, 7] as const

export const BRISTOL_LABELS: Record<Bristol, string> = {
  1: '1 — billes dures séparées',
  2: '2 — en saucisse, grumeleuse',
  3: '3 — en saucisse, craquelée',
  4: '4 — lisse et molle (normal)',
  5: '5 — morceaux mous, bords nets',
  6: '6 — morceaux pâteux, bords déchiquetés',
  7: '7 — entièrement liquide',
}

export const BRISTOL_COURT: Record<Bristol, string> = {
  1: 'Billes dures',
  2: 'Grumeleuse',
  3: 'Craquelée',
  4: 'Normale',
  5: 'Morceaux mous',
  6: 'Pâteuse',
  7: 'Liquide',
}

/** Seuils de derivation vers les types analyses. */
export const BRISTOL_MOU_MIN: Bristol = 6
export const BRISTOL_DUR_MAX: Bristol = 2
/** Au-dela, la douleur a la defecation devient un evenement analysable. */
export const DOULEUR_DEFECATION_MIN = 5

/**
 * Une sensation ressentie : **evenement ponctuel**, pas un intervalle.
 *
 * Le modele a duree qui precedait obligeait a cloturer chaque episode, et un
 * oubli de cloture etait silencieusement catastrophique : une fin `null` etant
 * traitee comme infinie, tous les repas suivants sortaient de l'analyse pour ce
 * symptome. La duree d'une poussee est desormais **deduite** du regroupement
 * d'evenements rapproches (cf. `analysis/evenements.ts`).
 */
export interface Sensation {
  id: Id
  at: Instant
  type: SensationType
  /** Intensite ressentie sur le moment, 0 a 10. */
  intensite: number
  notes?: string
  creeLe: Instant
  modifieLe: Instant
}

/** Un passage aux selles : evenement ponctuel lui aussi. */
export interface Stool {
  id: Id
  at: Instant
  bristol: Bristol
  /** Besoin pressant, impossible a differer. */
  urgence: boolean
  /** Douleur ressentie a la defecation, 0 a 10. */
  douleur?: number
  /** Impression de ne pas avoir termine. */
  evacuationIncomplete?: boolean
  notes?: string
  creeLe: Instant
  modifieLe: Instant
}

/**
 * Pousee reconstituee a partir d'evenements rapproches.
 *
 * N'est jamais stockee : c'est la forme dans laquelle le moteur d'analyse
 * raisonne, produite a la volee par `analysis/evenements.ts`.
 */
export interface SymptomEpisode {
  id: Id
  type: SymptomType
  debutAt: Instant
  finAt: Instant | null
  /** Intensite de pic sur la poussee, 0 a 10. */
  intensite: number
  notes?: string
  creeLe: Instant
  modifieLe: Instant
}

// ---------------------------------------------------------------------------
// Journal quotidien (facteurs confondants)
// ---------------------------------------------------------------------------

export interface DailyLog {
  /** Cle primaire : jour local `YYYY-MM-DD`. */
  date: DayKey
  /** 0 = aucun stress, 10 = stress maximal. */
  stress: number | null
  heuresSommeil: number | null
  /** 0 = tres mauvaise nuit, 10 = excellente. */
  qualiteSommeil: number | null
  notes?: string
  modifieLe: Instant
}

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export interface MetaEntry {
  cle: string
  valeur: unknown
}
